import { isApiError } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, type IconName } from "@sagot/ui";
import { useId } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";

/** True when the API answered 402 (the feature isn't on the workspace's plan). */
export function isPlanGateError(error: unknown): boolean {
  return isApiError(error) && error.isPlanGate;
}

interface UpgradeCardProps {
  title: string;
  body: string;
  icon?: IconName;
  className?: string;
}

/** 402 plan-gate state: what the feature does + a link to Billing to upgrade. */
export function UpgradeCard({ title, body, icon = "lock", className }: UpgradeCardProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const titleId = useId();
  return (
    <section className={`card card-body flex flex-col gap-4 md:flex-row md:items-center ${className ?? ""}`} aria-labelledby={titleId} data-testid="upgrade-card">
      <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-ink">
        <Icon name={icon} className="size-6" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 id={titleId} className="text-base font-bold">
            {title}
          </h2>
          <Badge tone="warning">{t("billing.gate.badge")}</Badge>
        </div>
        <p className="mt-1 text-sm text-fg-muted">{body}</p>
      </div>
      <Button asChild>
        <AppLink href={consoleRoutes.billing(summary.slug)}>
          <Icon name="arrow-up-right" />
          {t("billing.gate.upgrade")}
        </AppLink>
      </Button>
    </section>
  );
}
